import React, { useState } from 'react';
import styles from '../styles/panelList.module.css';

function getYouTubeVideoId(urlOrId: string): string | null {
  if (!urlOrId || typeof urlOrId !== 'string') return null;
  const trimmed = urlOrId.trim();
  if (!trimmed) return null;
  if (!trimmed.includes('youtube.com') && !trimmed.includes('youtu.be')) {
    return trimmed.length > 0 ? trimmed : null;
  }
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/
  ];
  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match) return match[1];
  }
  return null;
}

interface PanelItem {
  title: string;
  subtext: string;
  youtube_url: string;
}

interface PanelListVideosProps {
  title: string;
  subtext: string;
  items: PanelItem[];
  isExpanded?: boolean;
  iconUrl?: string;
  accentColor?: string;
  showItemCount?: boolean;
}

const PanelListVideos: React.FC<PanelListVideosProps> = ({
  title,
  subtext,
  items,
  isExpanded = false,
  iconUrl,
  accentColor = '#007bff',
  showItemCount = true
}) => {
  const [expanded, setExpanded] = useState(isExpanded);

  const toggleExpanded = () => setExpanded(!expanded);

  const headerStyle: React.CSSProperties = accentColor ? { borderLeft: `4px solid ${accentColor}` } : {};

  return (
    <div className={styles.panel}>
      <div
        className={styles.panelHeader}
        onClick={toggleExpanded}
        style={headerStyle}
      >
        <div className={styles.panelTitleSection}>
          <div className={styles.panelTitleRow}>
            {iconUrl && (
              <img
                src={iconUrl}
                alt=""
                className={styles.panelIcon}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <div className={styles.panelTitleContent}>
              <div className={styles.panelTitleRowTop}>
                <h3 className={styles.panelTitle}>{title}</h3>
                {showItemCount && items.length > 0 && (
                  <span className={styles.itemCountBadge}>{items.length}</span>
                )}
              </div>
              {subtext && <p className={styles.panelSubtext}>{subtext}</p>}
            </div>
          </div>
        </div>
        <button
          className={`${styles.expandButton} ${expanded ? styles.expanded : ''}`}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>

      {expanded && (
        <div className={styles.panelContent}>
          <div className={styles.videoListGrid}>
            {items.map((item, index) => {
              const videoId = getYouTubeVideoId(item.youtube_url || '');
              const embedUrl = videoId
                ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`
                : null;
              const thumbnailUrl = videoId
                ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
                : null;

              return (
                <div key={index} className={styles.videoListItem}>
                  <div className={styles.videoItemContent}>
                    {embedUrl ? (
                      <div className={styles.videoEmbedWrap}>
                        <iframe
                          src={embedUrl}
                          title={item.title || `Video ${index + 1}`}
                          className={styles.videoEmbedSmall}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div className={styles.videoPlaceholder}>
                        {thumbnailUrl ? (
                          <img src={thumbnailUrl} alt="" className={styles.videoThumbImg} />
                        ) : (
                          <span>No video URL</span>
                        )}
                        {item.youtube_url && !videoId && (
                          <span className={styles.videoInvalidUrl}>Invalid YouTube URL</span>
                        )}
                      </div>
                    )}
                    <div className={styles.itemContent}>
                      <h4 className={styles.itemTitle}>{item.title || 'Untitled'}</h4>
                      {item.subtext && <p className={styles.itemSubtext}>{item.subtext}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelListVideos;
